import { ChangeDetectionStrategy, Component, computed, inject, Input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { Store } from '@ngrx/store';
import { ResponsiveService } from '../../../shared/services/responsive.service';
import { PSIC_COMPONENTS } from '@pacificspecialtyinsurancecompany/psic-ng-components';
import { formatAmount } from '../../../shared/utils';
import { StepUpAuthService } from '../../../shared/services/step-up-auth.service';
import { CardLoaderComponent } from '../../../shared/components/card-loader/card-loader.component';

interface PaymentHistoryItem {
  entryDate: string;
  amount: string;
  paymentMethod: string;
  cardNumber: string;
  cardType: string;
  bankName: string;
  bankAccountNumber: string;
  checkNumber: string;
  paymentMethodCode: string;
  displayPaymentMethod: string;
  displayCardNumber: string;
}

@Component({
  selector: 'app-payment-history',
  standalone: true,
  imports: [CommonModule, MatIconModule, ...PSIC_COMPONENTS, CardLoaderComponent],
  templateUrl: './payment-history.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentHistoryComponent {
  @Input() dcBillingFailed: any = false;

  private readonly responsive = inject(ResponsiveService);
  private readonly store = inject(Store);
  private readonly stepUpAuth = inject(StepUpAuthService);

  readonly isMobile = toSignal(this.responsive.isMobile$, { initialValue: false });
  readonly showMoreDataLimit = 3;

  private readonly policyNumber = this.stepUpAuth.getPolicyNumber();

  private readonly data = toSignal(
    this.store.select((state: any) => state.policy?.data),
    { initialValue: null }
  );
  readonly isLoading = toSignal(
    this.store.select((state: any) => state.policy.loading),
    { initialValue: true }
  );

  private readonly rawPaymentHistory = computed(() => {
    const billingSnapshot = this.data()?.[this.policyNumber]?.billingSnapshot;
    return billingSnapshot?.paymentHistory ?? [];
  });

  private readonly mappedPaymentHistory = computed<PaymentHistoryItem[]>(() =>
    this.rawPaymentHistory().map((item: any) => ({
      entryDate: item.entryDate,
      amount: item.amount,
      paymentMethod: item.paymentMethod,
      cardNumber: item.cardNumber ?? '',
      cardType: item.cardType ?? '',
      bankName: item.bankName ?? '',
      bankAccountNumber: item.bankAccountNumber ?? '',
      checkNumber: item.checkNumber ?? '',
      paymentMethodCode: item.paymentMethodCode,
      displayPaymentMethod: this.getPaymentMethod(item),
      displayCardNumber: this.getCardNumber(item),
    }))
  );

  readonly activeTab = signal<string>('payments');

  readonly historyField: any = {
    name: 'historySelector',
    id: 'historySelector',
    type: 'choice-chip',
    value: 'payments',
    options: {
      optionsList: [
        { id: 'payments', caption: 'Payments', value: 'payments', required: true },
        { id: 'refunds', caption: 'Refunds', value: 'refunds', required: true },
      ],
    },
  };

  handleTabSelected(event: any): void {
    this.activeTab.set(event);
  }

  readonly showMorePayments = signal(false);
  readonly showMoreRefunds = signal(false);

  readonly paymentHistoryCount = computed(() => this.mappedPaymentHistory().length);
  readonly refundHistoryCount = computed(() => 0);

  readonly paymentHistoryList = computed<PaymentHistoryItem[]>(() => {
    const list = this.mappedPaymentHistory();
    return this.showMorePayments() ? list : list.slice(0, this.showMoreDataLimit);
  });

  readonly refundHistoryList = computed<PaymentHistoryItem[]>(() => []);

  onShowPayments(): void {
    this.showMorePayments.update((v) => !v);
  }

  onShowRefunds(): void {
    this.showMoreRefunds.update((v) => !v);
  }

  formatAmount(amount?: string): string {
    return formatAmount(amount);
  }

  getCardNumber(item: any): string {
    //CC - Credit Card
    //EFTC - Electronic Funds Transfer Consumer
    //CK - Consumer Check
    //CS - Cash
    //PC - Producer Check
    let paymentMethod = item.paymentMethodCode;
    switch (paymentMethod) {
      case 'CC':
        return `•••• ${item.cardNumber}`;
      case 'EFTC':
        return `•••• ${item.bankAccountNumber}`;
      case 'CK':
        return `•••• ${item.checkNumber}`;
      case 'CS':
      case 'PC':
        return '';
      default:
        return '';
    }
  }

  getPaymentMethod(item: any): string {
    //CC - Credit Card
    //EFTC - Electronic Funds Transfer Consumer
    //CK - Consumer Check
    //CS - Cash
    //PC - Producer Check
    let paymentMethod = item.paymentMethodCode;
    switch (paymentMethod) {
      case 'CC':
        return item.cardType;
      case 'EFTC':
        return item.bankName;
      case 'CK':
        return item.paymentMethod;
      case 'CS':
        return item.paymentMethod;
      case 'PC':
        return item.paymentMethod;
      default:
        return '';
    }
  }
}
